/**
 * Franchises Management Component - Super Admin
 */
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { 
  Building2, Plus, Edit2, Trash2, MapPin, Phone, Mail, 
  DollarSign, ChevronDown, ChevronUp, Save, X, Loader2,
  CreditCard, CheckCircle, Clock
} from "lucide-react";
import { authAxios } from "@/utils/auth";

const EMPTY_FRANCHISE = {
  slug: "",
  name: "",
  display_name: "",
  logo_url: "",
  contact: {
    phone: "",
    email: "",
    website: ""
  },
  branding: {
    primary_color: "#1e40af",
    secondary_color: "#10b981",
    hero_image_url: "",
    tagline: "Exterior Cleaning Made Simple"
  },
  service_area_zips: [],
  notification_emails: [],
  sms_alert_number: "",
  monthly_fee: 0,
  notes: ""
};

export default function FranchisesManagement() {
  const [searchParams] = useSearchParams();
  const [franchises, setFranchises] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FRANCHISE);
  const [zipInput, setZipInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  
  // Subscription state
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [subscribingFranchise, setSubscribingFranchise] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const fetchFranchises = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/franchises?include_inactive=true");
      setFranchises(res.data);
    } catch {
      toast.error("Failed to load franchises");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await authAxios.get("/franchises/stats");
      setStats(res.data);
    } catch (error) {
      // Stats are non-critical - log but don't show toast
      console.warn("Failed to load franchise stats:", error.message);
    }
  }, []);

  const fetchSubscriptionPlans = useCallback(async () => {
    try {
      const res = await authAxios.get("/payments/plans");
      setSubscriptionPlans(res.data.plans || []);
    } catch (error) {
      console.warn("Failed to load subscription plans:", error.message);
    }
  }, []);

  // Check for payment return from Stripe
  const checkPaymentStatus = useCallback(async (sessionId) => {
    setCheckingPayment(true);
    try {
      const res = await authAxios.get(`/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        toast.success(`Payment successful! ${res.data.plan} subscription activated.`);
        fetchFranchises(); // Refresh to show updated subscription status
      } else if (res.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
      } else {
        // Still pending, poll again
        setTimeout(() => checkPaymentStatus(sessionId), 2000);
        return;
      }
    } catch (error) {
      toast.error("Failed to verify payment status");
    }
    setCheckingPayment(false);
  }, [fetchFranchises]);

  // Handle Stripe redirect
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    
    if (paymentStatus === "success" && sessionId) {
      checkPaymentStatus(sessionId);
      // Clean up URL
      window.history.replaceState({}, "", "/admin/franchises");
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment was cancelled");
      window.history.replaceState({}, "", "/admin/franchises");
    }
  }, [searchParams, checkPaymentStatus]);

  useEffect(() => {
    fetchFranchises();
    fetchStats();
    fetchSubscriptionPlans();
  }, [fetchFranchises, fetchStats, fetchSubscriptionPlans]);

  const initiateSubscription = async (franchiseId, planId) => {
    setSubscribingFranchise(franchiseId);
    setSelectedPlan(planId);
    
    try {
      const res = await authAxios.post("/payments/subscribe", {
        franchise_id: franchiseId,
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start subscription");
      setSubscribingFranchise(null);
      setSelectedPlan(null);
    }
  };

  const startEdit = (franchise) => {
    setFormData({
      slug: franchise.slug || "",
      name: franchise.name || "",
      display_name: franchise.display_name || "",
      logo_url: franchise.logo_url || "",
      contact: {
        phone: franchise.contact?.phone || "",
        email: franchise.contact?.email || "",
        website: franchise.contact?.website || ""
      },
      branding: {
        primary_color: franchise.branding?.primary_color || "#1e40af",
        secondary_color: franchise.branding?.secondary_color || "#10b981",
        hero_image_url: franchise.branding?.hero_image_url || "",
        tagline: franchise.branding?.tagline || "Exterior Cleaning Made Simple"
      },
      service_area_zips: franchise.service_area_zips || [],
      notification_emails: franchise.notification_emails || [],
      sms_alert_number: franchise.sms_alert_number || "",
      monthly_fee: franchise.monthly_fee || 0,
      notes: franchise.notes || ""
    });
    setEditingId(franchise.id);
    setExpandedId(franchise.id);
  };

  const startAdd = () => {
    setFormData(EMPTY_FRANCHISE);
    setShowAddForm(true);
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData(EMPTY_FRANCHISE);
  };

  const addZip = () => {
    const zip = zipInput.trim();
    if (zip && /^\d{5}$/.test(zip) && !formData.service_area_zips.includes(zip)) {
      setFormData(prev => ({
        ...prev,
        service_area_zips: [...prev.service_area_zips, zip]
      }));
      setZipInput("");
    } else if (zip && !/^\d{5}$/.test(zip)) {
      toast.error("ZIP code must be 5 digits");
    }
  };

  const removeZip = (zip) => {
    setFormData(prev => ({
      ...prev,
      service_area_zips: prev.service_area_zips.filter(z => z !== zip)
    }));
  };

  const addEmail = () => {
    const email = emailInput.trim().toLowerCase();
    if (email && email.includes("@") && !formData.notification_emails.includes(email)) {
      setFormData(prev => ({
        ...prev,
        notification_emails: [...prev.notification_emails, email]
      }));
      setEmailInput("");
    }
  };

  const removeEmail = (email) => {
    setFormData(prev => ({
      ...prev,
      notification_emails: prev.notification_emails.filter(e => e !== email)
    }));
  };

  const saveFranchise = async () => {
    if (!formData.name || !formData.slug || !formData.display_name) {
      toast.error("Name, slug, and display name are required");
      return;
    }
    if (!formData.contact.phone || !formData.contact.email) {
      toast.error("Contact phone and email are required");
      return;
    }
    if (formData.service_area_zips.length === 0) {
      toast.error("At least one ZIP code is required");
      return;
    }
    if (formData.notification_emails.length === 0) {
      toast.error("At least one notification email is required");
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await authAxios.put(`/franchises/${editingId}`, formData);
        toast.success("Franchise updated");
      } else {
        await authAxios.post("/franchises", formData);
        toast.success("Franchise created");
      }
      cancelEdit();
      fetchFranchises();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save franchise");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (franchiseId, currentStatus) => {
    try {
      await authAxios.patch(`/franchises/${franchiseId}/status?is_active=${!currentStatus}`);
      toast.success(`Franchise ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchFranchises();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const deleteFranchise = async (franchiseId) => {
    if (!window.confirm("Are you sure you want to delete this franchise? This will deactivate it.")) return;
    
    try {
      await authAxios.delete(`/franchises/${franchiseId}`);
      toast.success("Franchise deleted");
      fetchFranchises();
      fetchStats();
    } catch {
      toast.error("Failed to delete franchise");
    }
  };

  const getStatusBadge = (franchise) => {
    if (franchise.is_default) return <Badge className="bg-purple-100 text-purple-800">Default</Badge>;
    if (!franchise.is_active) return <Badge className="bg-red-100 text-red-800">Inactive</Badge>;
    
    const status = franchise.subscription_status || "active";
    const styles = {
      active: "bg-emerald-100 text-emerald-800",
      trial: "bg-blue-100 text-blue-800",
      suspended: "bg-yellow-100 text-yellow-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return <Badge className={styles[status]}>{status}</Badge>;
  };

  const renderForm = () => (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>URL Slug *</Label>
          <Input
            value={formData.slug}
            onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '')})}
            placeholder="e.g., chicago-north"
            disabled={!!editingId}
            data-testid="franchise-slug"
          />
          <p className="text-xs text-slate-500">Used in URL: /chicago-north</p>
        </div>
        <div className="space-y-2">
          <Label>Business Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="e.g., ABC Power Washing LLC"
            data-testid="franchise-name"
          />
        </div>
        <div className="space-y-2">
          <Label>Display Name *</Label>
          <Input
            value={formData.display_name}
            onChange={(e) => setFormData({...formData, display_name: e.target.value})}
            placeholder="e.g., Chicago North"
            data-testid="franchise-display-name"
          />
        </div>
      </div>

      {/* Contact Info */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Phone *</Label>
          <Input
            value={formData.contact.phone}
            onChange={(e) => setFormData({...formData, contact: {...formData.contact, phone: e.target.value}})}
            placeholder="(555) 123-4567"
            data-testid="franchise-phone"
          />
        </div>
        <div className="space-y-2">
          <Label>Email *</Label>
          <Input
            value={formData.contact.email}
            onChange={(e) => setFormData({...formData, contact: {...formData.contact, email: e.target.value}})}
            placeholder="contact@business.com"
            data-testid="franchise-email"
          />
        </div>
        <div className="space-y-2">
          <Label>Website</Label>
          <Input
            value={formData.contact.website}
            onChange={(e) => setFormData({...formData, contact: {...formData.contact, website: e.target.value}})}
            placeholder="www.business.com"
          />
        </div>
      </div>

      {/* Branding */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Logo URL</Label>
          <Input
            value={formData.logo_url}
            onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
            placeholder="https://..."
          />
        </div>
        <div className="space-y-2">
          <Label>Tagline</Label>
          <Input
            value={formData.branding.tagline}
            onChange={(e) => setFormData({...formData, branding: {...formData.branding, tagline: e.target.value}})}
            placeholder="Your cleaning solution"
          />
        </div>
      </div>

      {/* Service Area ZIPs */}
      <div className="space-y-2">
        <Label>Service Area ZIP Codes *</Label>
        <div className="flex gap-2">
          <Input
            value={zipInput}
            onChange={(e) => setZipInput(e.target.value)}
            placeholder="Enter ZIP code"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addZip())}
            className="max-w-[150px]"
            data-testid="zip-input"
          />
          <Button type="button" variant="outline" onClick={addZip}>Add</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {formData.service_area_zips.map(zip => (
            <Badge key={zip} variant="secondary" className="px-2 py-1">
              {zip}
              <button onClick={() => removeZip(zip)} className="ml-2 text-red-500 hover:text-red-700">×</button>
            </Badge>
          ))}
        </div>
        <p className="text-xs text-slate-500">{formData.service_area_zips.length} ZIP codes</p>
      </div>

      {/* Notification Emails */}
      <div className="space-y-2">
        <Label>Notification Emails *</Label>
        <div className="flex gap-2">
          <Input
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="email@example.com"
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
            className="max-w-[300px]"
            data-testid="email-input"
          />
          <Button type="button" variant="outline" onClick={addEmail}>Add</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {formData.notification_emails.map(email => (
            <Badge key={email} variant="secondary" className="px-2 py-1">
              {email}
              <button onClick={() => removeEmail(email)} className="ml-2 text-red-500 hover:text-red-700">×</button>
            </Badge>
          ))}
        </div>
      </div>

      {/* SMS and Fee */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>SMS Alert Number</Label>
          <Input
            value={formData.sms_alert_number}
            onChange={(e) => setFormData({...formData, sms_alert_number: e.target.value})}
            placeholder="+15551234567"
          />
        </div>
        <div className="space-y-2">
          <Label>Monthly Fee ($)</Label>
          <Input
            type="number"
            value={formData.monthly_fee}
            onChange={(e) => setFormData({...formData, monthly_fee: parseFloat(e.target.value) || 0})}
            placeholder="99"
            data-testid="monthly-fee"
          />
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label>Admin Notes</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          placeholder="Internal notes about this franchise..."
          rows={2}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={saveFranchise} disabled={saving} data-testid="save-franchise-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {editingId ? "Update Franchise" : "Create Franchise"}
        </Button>
        <Button variant="outline" onClick={cancelEdit}>
          <X className="w-4 h-4 mr-2" /> Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <div data-testid="franchises-management">
      {/* Payment Processing Overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-lg font-medium">Verifying Payment...</p>
            <p className="text-sm text-slate-500">Please wait while we confirm your payment.</p>
          </Card>
        </div>
      )}

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
          Franchise Management
        </h1>
        <Button onClick={startAdd} data-testid="add-franchise-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Franchise
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Franchises</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total_franchises}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Active</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.active_franchises}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Monthly Revenue</p>
              <p className="text-2xl font-bold text-blue-600">${stats.monthly_recurring_revenue}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Quotes</p>
              <p className="text-2xl font-bold text-purple-600">
                {stats.quotes_by_franchise?.reduce((sum, f) => sum + f.count, 0) || 0}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add New Franchise Form */}
      {showAddForm && (
        <Card className="border-blue-200 bg-blue-50/50 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Add New Franchise
            </CardTitle>
          </CardHeader>
          <CardContent>{renderForm()}</CardContent>
        </Card>
      )}

      {/* Franchise List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
      ) : franchises.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No franchises yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {franchises.map((franchise) => (
            <Card 
              key={franchise.id || franchise.slug} 
              className={`border-slate-200 ${!franchise.is_active ? 'opacity-60' : ''} ${editingId === franchise.id ? 'ring-2 ring-blue-500' : ''}`}
              data-testid={`franchise-card-${franchise.slug}`}
            >
              <CardContent className="p-6">
                {editingId === franchise.id ? (
                  renderForm()
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-slate-900">{franchise.name}</h3>
                          {getStatusBadge(franchise)}
                          <Badge variant="outline" className="font-mono">/{franchise.slug}</Badge>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-2 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" /> {franchise.contact?.phone}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" /> {franchise.contact?.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" /> {franchise.service_area_zips?.length || 0} ZIP codes
                          </span>
                        </div>
                        {franchise.monthly_fee > 0 && (
                          <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
                            <DollarSign className="w-4 h-4" /> ${franchise.monthly_fee}/month
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {!franchise.is_default && (
                          <>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-slate-500">Active</Label>
                              <Switch
                                checked={franchise.is_active}
                                onCheckedChange={() => toggleStatus(franchise.id, franchise.is_active)}
                              />
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => startEdit(franchise)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => deleteFranchise(franchise.id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedId(expandedId === franchise.id ? null : franchise.id)}
                        >
                          {expandedId === franchise.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    {expandedId === franchise.id && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        {/* Subscription Section */}
                        {!franchise.is_default && (
                          <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-blue-600" />
                                <span className="font-medium text-slate-800">Subscription</span>
                              </div>
                              {franchise.subscription_status === "active" ? (
                                <Badge className="bg-emerald-100 text-emerald-800">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Active
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-800">
                                  <Clock className="w-3 h-3 mr-1" /> Inactive
                                </Badge>
                              )}
                            </div>
                            
                            {franchise.subscription_status === "active" ? (
                              <div className="text-sm text-slate-600">
                                <p>Plan: <span className="font-medium capitalize">{franchise.subscription_plan}</span></p>
                                <p>Amount: <span className="font-medium">${franchise.subscription_amount}/month</span></p>
                                {franchise.last_payment_date && (
                                  <p>Last Payment: {new Date(franchise.last_payment_date).toLocaleDateString()}</p>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <p className="text-sm text-slate-600">Select a subscription plan:</p>
                                <div className="grid grid-cols-3 gap-2">
                                  {subscriptionPlans.map((plan) => (
                                    <Button
                                      key={plan.id}
                                      variant={selectedPlan === plan.id && subscribingFranchise === franchise.id ? "default" : "outline"}
                                      size="sm"
                                      className="flex flex-col h-auto py-2"
                                      onClick={() => initiateSubscription(franchise.id, plan.id)}
                                      disabled={subscribingFranchise === franchise.id}
                                      data-testid={`subscribe-${plan.id}`}
                                    >
                                      {subscribingFranchise === franchise.id && selectedPlan === plan.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <>
                                          <span className="font-semibold">${plan.amount}</span>
                                          <span className="text-xs opacity-75">{plan.name}</span>
                                        </>
                                      )}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-slate-700 mb-2">Service Area</p>
                          <div className="flex flex-wrap gap-1">
                            {franchise.service_area_zips?.slice(0, 20).map(zip => (
                              <Badge key={zip} variant="outline" className="text-xs">{zip}</Badge>
                            ))}
                            {franchise.service_area_zips?.length > 20 && (
                              <Badge variant="outline" className="text-xs">+{franchise.service_area_zips.length - 20} more</Badge>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-700 mb-2">Notifications</p>
                          <div className="space-y-1 text-sm text-slate-600">
                            {franchise.notification_emails?.map(email => (
                              <p key={email} className="flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {email}
                              </p>
                            ))}
                            {franchise.sms_alert_number && (
                              <p className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {franchise.sms_alert_number}
                              </p>
                            )}
                          </div>
                        </div>
                        {franchise.notes && (
                          <div className="md:col-span-2">
                            <p className="text-sm font-medium text-slate-700 mb-1">Notes</p>
                            <p className="text-sm text-slate-500">{franchise.notes}</p>
                          </div>
                        )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
